import type { LucideIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Cards'
import { Badge } from '@/components/Badge'
import { formatDateSimple } from '../../../../utils/formatter'
import { useNavigate } from 'react-router-dom'
import type { Demanda } from '@/models'
import { extractPlainText } from '@/utils/formatter'
import { ROUTES } from '@/routes/constants'
import { cn } from '@/utils/formatter'
import type { IconType } from 'react-icons'
import { Clock } from 'lucide-react'
import { VerticalScrollNavigator } from '@/components/ScrollNavigator'

interface DemandListProps {
  title: string
  demands: Demanda[]
  icon: LucideIcon
  colorClass: string
  emptyMessage?: string
  emptyIcon?: IconType
  indicatorColor?: string // Nova prop para cor do indicador lateral
  showUnassignedBadge?: boolean // Prop para controlar exibição do badge "Não atribuída"
  cardBackground?: string // Nova prop para background customizado dos cards
  highlightOverdue?: boolean // Nova prop para destacar cards atrasados com fundo vermelho
}

export const DemandList = ({ title, demands, icon: Icon, colorClass, emptyMessage, emptyIcon: EmptyIcon, indicatorColor, showUnassignedBadge = false, cardBackground, highlightOverdue = false }: DemandListProps) => {
  const navigate = useNavigate()

  const handleViewDemand = (demandId: number) => {
    navigate(ROUTES.DEMANDS.VIEW(String(demandId), { type: 'all' }))
  }

  // Função para verificar se uma demanda está atrasada (prazo)
  const isOverdue = (demand: Demanda): boolean => {
    if (!demand.sugestao_entrega) return false
    if (demand.status === 'FINALIZADA' || demand.status === 'SOLUCIONADA') return false
    
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    // Converte a data string para Date no fuso local
    const [year, month, day] = demand.sugestao_entrega.split('-').map(Number)
    const deadline = new Date(year, month - 1, day)
    deadline.setHours(0, 0, 0, 0)
    
    return deadline < today
  }

  // Função para verificar se a previsão está atrasada
  const isPrevisaoOverdue = (demand: Demanda): boolean => {
    if (!demand.previsao_entrega) return false
    if (demand.status === 'FINALIZADA' || demand.status === 'SOLUCIONADA') return false
    
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    // Converte a data string para Date no fuso local
    const [year, month, day] = demand.previsao_entrega.split('-').map(Number)
    const previsao = new Date(year, month - 1, day)
    previsao.setHours(0, 0, 0, 0)
    
    return previsao < today
  }

  // Função para verificar se uma demanda tem responsável atribuído
  const hasAssignee = (demand: Demanda): boolean => {
    // Verifica se tem demandado_fk (campo único)
    if (demand.demandado_fk) return true
    
    // Verifica se tem demandados (array) com pelo menos um responsável
    if (demand.demandados && demand.demandados.length > 0) return true
    
    // Verifica se tem atribuidas (array) com pelo menos uma atribuição ativa
    if (demand.atribuidas && demand.atribuidas.length > 0) return true
    
    return false
  }

  return (
    <Card className='h-full flex flex-col'>
      <CardHeader className='pb-3'>
        <CardTitle className='flex items-center gap-2'>
          <div className={`${colorClass} p-2 rounded-lg`}>
            <Icon className='h-5 w-5 text-white' />
          </div>
          <div>
            <div className='text-lg font-semibold'>{title}</div>
            <div className='text-sm text-muted-foreground font-normal'>
              {demands.length} {demands.length === 1 ? 'demanda' : 'demandas'}
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className='flex-1 pt-0 overflow-hidden'>
        {demands.length === 0 ? (
          <div className='flex h-full flex-col items-center justify-center gap-3 text-center text-muted-foreground'>
            <div className={`${colorClass} flex h-12 w-12 items-center justify-center rounded-xl text-white`}>
              {EmptyIcon ? <EmptyIcon className='h-6 w-6' /> : <Icon className='h-6 w-6' />}
            </div>
            <div className='max-w-[220px] text-sm font-medium leading-relaxed'>
              {emptyMessage ?? 'Nenhuma demanda encontrada.'}
            </div>
          </div>
        ) : (
          <VerticalScrollNavigator
            className='h-full'
            contentClassName='space-y-2'
            hideButtonsWhenDisabled={true}
            showScrollbar={false}
          >
            {demands.map(demand => {
              const isCardOverdue = highlightOverdue && isOverdue(demand)
              
              return (
              <div
                key={demand.id}
                onClick={() => demand.id && handleViewDemand(demand.id)}
                className={cn(
                  'group relative cursor-pointer rounded-lg border p-3',
                  'transition-all duration-200 hover:shadow-md',
                  isCardOverdue 
                    ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50 hover:border-red-400 dark:hover:border-red-600'
                    : cn(
                        'border-gray-200 dark:border-neutral-700 hover:border-blue-300 dark:hover:border-blue-700',
                        cardBackground || 'bg-white dark:bg-neutral-800 hover:bg-blue-50/50 dark:hover:bg-blue-900/10'
                      )
                )}
              >
                  {/* Indicador de status - agora usa a cor da coluna, ou vermelho se atrasado */}
                  <div className={cn(
                    'absolute left-0 top-0 bottom-0 w-1 rounded-l-lg transition-all duration-200',
                    isCardOverdue ? 'bg-red-500' : (indicatorColor || 'bg-blue-500'), // Vermelho se atrasado
                    'group-hover:w-1.5'
                  )} />

                  <div className='flex items-start justify-between gap-2 ml-2 min-w-0'>
                    <div className='min-w-0 flex-1 overflow-hidden'>
                      <div className='flex items-center gap-2 mb-1 min-w-0'>
                        <h4 className='line-clamp-1 text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-700 dark:group-hover:text-blue-400 transition-colors min-w-0 flex-1'>
                          {demand.titulo}
                        </h4>
                        {/* Badge de não atribuída - só aparece quando habilitado e sem responsável */}
                        {showUnassignedBadge && !hasAssignee(demand) && (
                          <Badge
                            variant='default'
                            size='sm'
                            className='shrink-0 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-300 text-[10px] px-1.5 py-0'
                          >
                            Não atribuída
                          </Badge>
                        )}
                      </div>
                      <p className='mt-1 line-clamp-2 text-xs text-gray-600 dark:text-gray-400 min-w-0 break-words'>
                        {extractPlainText(demand.descricao)}
                      </p>
                    </div>
                    <Badge
                      variant={
                        demand.prioridade === 'Urgente'
                          ? 'danger'
                          : demand.prioridade === 'Alta'
                            ? 'warning'
                            : demand.prioridade === 'Media'
                              ? 'info'
                              : 'default'
                      }
                      size='sm'
                      className='shrink-0'
                    >
                      {demand.prioridade}
                    </Badge>
                  </div>

                  <div className='mt-2 ml-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400'>
                    <span className='font-medium text-gray-600 dark:text-gray-300'>#{demand.id}</span>
                    <div className='flex flex-col gap-1 items-end'>
                      {demand.previsao_entrega && (
                        <span className={cn(
                          'font-medium flex items-center gap-1',
                          isPrevisaoOverdue(demand) && 'text-orange-600 dark:text-orange-400'
                        )}>
                          {isPrevisaoOverdue(demand) && (
                            <Clock className='w-3 h-3' />
                          )}
                          <span className={isPrevisaoOverdue(demand) ? '' : 'text-gray-500 dark:text-gray-400'}>Previsão:</span>
                          <span className={isPrevisaoOverdue(demand) ? 'font-semibold' : 'text-gray-700 dark:text-gray-300'}>
                            {formatDateSimple(demand.previsao_entrega)}
                          </span>
                        </span>
                      )}
                      {demand.sugestao_entrega && (
                        <span className={cn(
                          'font-medium flex items-center gap-1',
                          isOverdue(demand) && 'text-orange-600 dark:text-orange-400'
                        )}>
                          {isOverdue(demand) && (
                            <Clock className='w-3 h-3' />
                          )}
                          <span className={isOverdue(demand) ? '' : 'text-gray-500 dark:text-gray-400'}>Prazo:</span>
                          <span className={isOverdue(demand) ? 'font-semibold' : 'text-gray-700 dark:text-gray-300'}>
                            {formatDateSimple(demand.sugestao_entrega)}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </VerticalScrollNavigator>
        )}
      </CardContent>
    </Card>
  )
}
